import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import logoAlbaker from "@/assets/logo-albaker.png";
import { ArrowUpRight } from "lucide-react";

const Login = () => {
  const { login, formatApiError } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  const from = loc.state?.from || "/";

  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(form.email.trim(), form.password);
      toast.success("تم تسجيل الدخول");
      // Always prefer role-based destination over `from` to avoid race with /admin redirect
      const target = u && u.role === "admin" ? "/admin" : (from && from !== "/login" ? from : "/");
      // Use replace to avoid back-button looping into /login
      navigate(target, { replace: true });
    } catch (err) {
      toast.error(formatApiError(err, "تعذّر تسجيل الدخول"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div dir="rtl" className="min-h-screen bg-ink text-bone flex items-center justify-center container-x py-20">
      <Toaster position="top-left" theme="dark" richColors />
      <div className="w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-3 mb-12 group" data-testid="login-back-home">
          <img src={logoAlbaker} alt="Albaker" className="h-10 w-auto" />
          <span className="overline">العودة للموقع</span>
        </Link>

        <div className="overline mb-4">تسجيل الدخول</div>
        <h1 className="font-arabic font-black text-4xl sm:text-5xl text-bone leading-[1.15] mb-10">
          أهلاً بعودتك.
        </h1>

        <form onSubmit={onSubmit} className="space-y-7" data-testid="login-form">
          <label className="block">
            <span className="block mb-2 text-xs tracking-[0.2em] uppercase text-white/50">البريد الإلكتروني</span>
            <input
              type="email"
              required
              autoComplete="email"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              className="editorial-input"
              data-testid="login-email"
              dir="ltr"
            />
          </label>
          <label className="block">
            <span className="block mb-2 text-xs tracking-[0.2em] uppercase text-white/50">كلمة المرور</span>
            <input
              type="password"
              required
              autoComplete="current-password"
              value={form.password}
              onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
              className="editorial-input"
              data-testid="login-password"
              dir="ltr"
            />
          </label>

          <button
            type="submit"
            disabled={loading}
            className="btn-gold group disabled:opacity-60"
            data-testid="login-submit"
          >
            <span>{loading ? "جاري الدخول…" : "دخول"}</span>
            <ArrowUpRight size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </button>

          <div className="text-sm text-white/55 font-arabic">
            <Link
              to="/forgot-password"
              className="hover:text-gold transition-colors"
              data-testid="login-forgot-link"
            >
              نسيت كلمة المرور؟
            </Link>
          </div>
        </form>

        <div className="mt-10 pt-8 border-t border-white/10 text-sm text-white/55 font-arabic">
          ليس لديك حساب؟{" "}
          <Link to="/register" className="text-gold hover:text-gold-light transition-colors" data-testid="login-register-link">
            إنشاء حساب
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Login;
